import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Paper,
  makeStyles,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  Chip,
  Tooltip,
  CircularProgress
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import AddIcon from "@material-ui/icons/Add";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import CancelIcon from "@material-ui/icons/Cancel";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { toast } from "react-toastify";
import api from "../../services/api";
import useConfirm from "../../hooks/useConfirm";
import connectToSocket from "../../services/socket-io";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2), margin: theme.spacing(1), display: "flex", flexDirection: "column", height: "calc(100vh - 80px)" },
  toolbar: { display: "flex", gap: theme.spacing(2), alignItems: "center", marginBottom: theme.spacing(1), flexWrap: "wrap" },
  field: { minWidth: 200 },
  board: { display: "flex", gap: theme.spacing(1.5), overflowX: "auto", flex: 1, paddingBottom: theme.spacing(1) },
  // As superfícies do quadro eram fixas em claro (`#f4f6f8` e `#fff`), mas o
  // texto herda `text.primary` do tema — que no modo escuro é claro. O
  // resultado era texto branco sobre coluna branca: o nome do estágio e o
  // título do negócio sumiam por completo, e só o valor (que tinha cor própria)
  // continuava legível.
  column: {
    minWidth: 280,
    width: 280,
    // Mais escura que a página, como no modo claro ela é mais escura que o
    // cartão: a coluna é a bandeja, o cartão é o que está apoiado nela. Clarear
    // a coluna inverteria essa leitura de profundidade.
    background:
      theme.palette.type === "dark"
        ? "rgba(0,0,0,0.28)"
        : "#f4f6f8",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    maxHeight: "100%"
  },
  columnHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(0.5, 1),
    fontWeight: 600,
    borderRadius: 4,
    color: theme.palette.text.primary
  },
  cards: { flex: 1, overflowY: "auto", marginTop: theme.spacing(1), display: "flex", flexDirection: "column", gap: theme.spacing(1) },
  card: {
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    padding: theme.spacing(1.2),
    borderRadius: 4,
    // Sombra clara não separa nada de um fundo escuro; ali quem dá o relevo é
    // a borda.
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 3px rgba(0,0,0,0.5)"
        : "0 1px 2px rgba(0,0,0,0.08)",
    border:
      theme.palette.type === "dark"
        ? `1px solid ${theme.palette.divider}`
        : "none",
    cursor: "grab",
    borderLeft: `3px solid ${theme.palette.primary.main}`
  },
  columnOver: { outline: `2px dashed ${theme.palette.primary.main}` },
  cardOver: { outline: `2px dashed ${theme.palette.primary.main}` },
  cardTitle: { fontWeight: 600, fontSize: 14, marginBottom: 4 },
  cardMeta: { fontSize: 12, color: theme.palette.text.secondary },
  cardActions: { display: "flex", gap: 4, marginTop: 4 },
  // Verde e vermelho escuros somem sobre cartão escuro; a paleta do tema já
  // tem a variante certa para cada modo.
  winIcon: { color: theme.palette.success.main },
  loseIcon: { color: theme.palette.error.main },
  dropTarget: { minHeight: 8 }
}));

const clientLabel = (c) => {
  if (!c) return "";
  if (c.personType === "PJ" || c.companyName || c.tradeName) {
    return c.tradeName || c.companyName || "";
  }
  return [c.firstName, c.lastName].filter(Boolean).join(" ") ||
    c.companyName || c.tradeName || c.email || "";
};

const emptyForm = {
  title: "",
  description: "",
  clientId: "",
  contactId: "",
  pipelineId: "",
  stageId: "",
  ownerId: "",
  value: 0,
  currency: "BRL",
  expectedCloseDate: ""
};

const Deals = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState("");
  const [deals, setDeals] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [clientLoading, setClientLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const clientSearchTimer = useRef(null);
  const [users, setUsers] = useState([]);
  const [lostReasons, setLostReasons] = useState([]);
  const [dialog, setDialog] = useState({ open: false, form: emptyForm, editing: null });
  const [loseDlg, setLoseDlg] = useState({ open: false, dealId: null, lostReasonId: "", note: "" });
  const [filter, setFilter] = useState({ searchParam: "", ownerId: "" });
  const [draggingId, setDraggingId] = useState(null);
  const [overStage, setOverStage] = useState(null);

  // load pipelines + lookups once
  useEffect(() => {
    (async () => {
      try {
        const [p, u, lr] = await Promise.all([
          api.get("/pipelines"),
          api.get("/users").catch(() => ({ data: { users: [] } })),
          api.get("/lost-reasons").catch(() => ({ data: [] }))
        ]);
        setPipelines(Array.isArray(p.data) ? p.data : p.data?.pipelines || []);
        const usersRaw = u.data?.users ?? u.data;
        setUsers(Array.isArray(usersRaw) ? usersRaw : []);
        const lrRaw = lr.data?.data ?? lr.data;
        setLostReasons(Array.isArray(lrRaw) ? lrRaw : []);
        const pipelinesArr = Array.isArray(p.data) ? p.data : p.data?.pipelines || [];
        const def = pipelinesArr.find((x) => x.isDefault) || pipelinesArr[0];
        if (def) setPipelineId(def.id);
      } catch {
        toast.error("Erro ao carregar dados iniciais");
      }
    })();
  }, []);

  const fetchDeals = useCallback(async () => {
    if (!pipelineId) return;
    try {
      const params = { pipelineId, pageSize: 200 };
      if (filter.searchParam) params.searchParam = filter.searchParam;
      if (filter.ownerId) params.ownerId = filter.ownerId;
      const { data } = await api.get("/deals", { params });
      setDeals(data.data || []);
    } catch {
      toast.error("Erro ao carregar deals");
    }
  }, [pipelineId, filter]);
  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  // socket realtime
  useEffect(() => {
    const socket = connectToSocket();
    socket.on("deal", () => fetchDeals());
    return () => socket.disconnect();
  }, [fetchDeals]);

  // busca assincrona de clientes (a partir de 3 letras)
  useEffect(() => {
    if (clientSearchTimer.current) clearTimeout(clientSearchTimer.current);
    const term = (clientSearch || "").trim();
    if (term.length < 3) {
      setClientLoading(false);
      return undefined;
    }
    setClientLoading(true);
    clientSearchTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/clients", { params: { searchParam: term } });
        const arr = data?.clients ?? data?.data ?? data?.contacts ?? data;
        const list = Array.isArray(arr) ? arr : [];
        setClientOptions((prev) => {
          if (selectedClient && !list.find((c) => c.id === selectedClient.id)) {
            return [selectedClient, ...list];
          }
          return list;
        });
      } catch {
        setClientOptions(selectedClient ? [selectedClient] : []);
      } finally {
        setClientLoading(false);
      }
    }, 300);
    return () => clientSearchTimer.current && clearTimeout(clientSearchTimer.current);
  }, [clientSearch, selectedClient]);

  const currentPipeline = useMemo(() => pipelines.find((p) => p.id === pipelineId), [pipelines, pipelineId]);
  const stages = useMemo(() => (currentPipeline?.stages || []).slice().sort((a, b) => a.order - b.order), [currentPipeline]);
  const dealsByStage = useMemo(() => {
    const map = {};
    stages.forEach((s) => { map[s.id] = []; });
    deals.forEach((d) => { if (map[d.stageId]) map[d.stageId].push(d); });
    return map;
  }, [stages, deals]);

  const totalsByStage = useMemo(() => {
    const t = {};
    Object.entries(dealsByStage).forEach(([sid, arr]) => {
      t[sid] = arr.reduce((sum, d) => sum + Number(d.value || 0), 0);
    });
    return t;
  }, [dealsByStage]);

  // dialog
  const openNew = () => {
    setSelectedClient(null);
    setClientOptions([]);
    setClientSearch("");
    setDialog({
      open: true,
      form: { ...emptyForm, pipelineId, stageId: stages[0]?.id || "" },
      editing: null
    });
  };
  const openEdit = (d) => {
    const preset = d.client && d.client.id ? d.client : (d.clientId ? { id: d.clientId } : null);
    setSelectedClient(preset);
    setClientOptions(preset ? [preset] : []);
    setClientSearch("");
    setDialog({
      open: true,
      form: { ...emptyForm, ...d, expectedCloseDate: d.expectedCloseDate || "", clientId: d.clientId ?? "", contactId: d.contactId ?? "", ownerId: d.ownerId ?? "" },
      editing: d
    });
    // se o deal traz apenas o id, busca o cliente para exibir o nome
    if (d.clientId && !(d.client && d.client.firstName)) {
      api.get(`/clients/${d.clientId}`).then(({ data }) => {
        const c = data?.client || data;
        if (c && c.id) {
          setSelectedClient(c);
          setClientOptions((opts) => {
            const exists = opts.find((o) => o.id === c.id);
            return exists ? opts : [c, ...opts];
          });
        }
      }).catch(() => {});
    }
  };
  const close = () => setDialog((d) => ({ ...d, open: false }));
  const setF = (patch) => setDialog((d) => ({ ...d, form: { ...d.form, ...patch } }));

  const save = async () => {
    try {
      const payload = { ...dialog.form };
      ["clientId", "contactId", "ownerId", "priceListId"].forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      if (!payload.expectedCloseDate) payload.expectedCloseDate = null;
      payload.value = Number(payload.value) || 0;
      if (dialog.editing) await api.put(`/deals/${dialog.editing.id}`, payload);
      else await api.post("/deals", payload);
      toast.success("Salvo");
      close();
      fetchDeals();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao salvar");
    }
  };

  const remove = async (d) => {
    if (!(await confirm({ title: `Excluir o negócio "${d.title}"?`, message: "O histórico de movimentação e os itens do negócio são removidos junto." }))) return;
    await api.delete(`/deals/${d.id}`);
    fetchDeals();
  };

  const moveTo = async (dealId, stageId) => {
    try {
      await api.post(`/deals/${dealId}/move`, { stageId });
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao mover");
    }
    fetchDeals();
  };

  const win = async (d) => {
    if (!(await confirm({ title: `Marcar "${d.title}" como ganho?`, message: "O negócio sai do funil de previsão e uma conta a receber é gerada.", destructive: false, confirmLabel: "Marcar como ganho" }))) return;
    await api.post(`/deals/${d.id}/win`);
    fetchDeals();
  };

  const openLose = (d) => setLoseDlg({ open: true, dealId: d.id, lostReasonId: "", note: "" });
  const confirmLose = async () => {
    await api.post(`/deals/${loseDlg.dealId}/lose`, {
      lostReasonId: loseDlg.lostReasonId || null,
      note: loseDlg.note || null
    });
    setLoseDlg({ open: false, dealId: null, lostReasonId: "", note: "" });
    fetchDeals();
  };

  // drag handlers
  const onDragStart = (e, dealId) => { setDraggingId(dealId); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e, sid) => { e.preventDefault(); setOverStage(sid); };
  const onDrop = (e, sid) => {
    e.preventDefault();
    setOverStage(null);
    if (draggingId == null) return;
    const deal = deals.find((d) => d.id === draggingId);
    if (deal && deal.stageId !== sid) moveTo(draggingId, sid);
    setDraggingId(null);
  };

  const fmtCur = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  return (
    <Paper className={classes.root}>
      {confirmDialog}
      <div className={classes.toolbar}>
        <Typography variant="h5" style={{ flexShrink: 0 }}>Pipeline de Vendas</Typography>
        <TextField select label="Pipeline" className={classes.field} value={pipelineId}
          onChange={(e) => setPipelineId(e.target.value)}>
          {pipelines.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </TextField>
        <TextField label="Buscar título" className={classes.field} value={filter.searchParam}
          onChange={(e) => setFilter((f) => ({ ...f, searchParam: e.target.value }))} />
        <TextField select label="Responsável" className={classes.field} value={filter.ownerId}
          onChange={(e) => setFilter((f) => ({ ...f, ownerId: e.target.value }))}>
          <MenuItem value="">Todos</MenuItem>
          {users.map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
        </TextField>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openNew} disabled={!stages.length}>Novo deal</Button>
      </div>

      <div className={classes.board}>
        {stages.map((s) => (
          <div
            key={s.id}
            className={`${classes.column}${overStage === s.id ? ` ${classes.columnOver}` : ""}`}
            onDragOver={(e) => onDragOver(e, s.id)}
            onDrop={(e) => onDrop(e, s.id)}
          >
            <div className={classes.columnHeader} style={{ borderLeft: `4px solid ${s.color || "#999"}` }}>
              <span>{s.name}</span>
              <Chip size="small" label={`${(dealsByStage[s.id] || []).length} • ${fmtCur(totalsByStage[s.id])}`} />
            </div>
            <div className={classes.cards}>
              {(dealsByStage[s.id] || []).map((d) => (
                <div
                  key={d.id}
                  className={classes.card}
                  draggable
                  onDragStart={(e) => onDragStart(e, d.id)}
                  style={{ ...(s.color ? { borderLeftColor: s.color } : {}), opacity: draggingId === d.id ? 0.5 : 1 }}
                >
                  <div className={classes.cardTitle}>{d.title}</div>
                  <div className={classes.cardMeta}>{fmtCur(d.value)} {d.currency && d.currency !== "BRL" ? d.currency : ""}</div>
                  {d.client && <div className={classes.cardMeta}>{clientLabel(d.client)}</div>}
                  {d.owner && <div className={classes.cardMeta}>👤 {d.owner.name}</div>}
                  {d.status !== "open" && <Chip size="small" label={d.status === "won" ? "Ganho" : "Perdido"} color={d.status === "won" ? "primary" : "secondary"} />}
                  <div className={classes.cardActions}>
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(d)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Marcar ganho"><IconButton size="small" onClick={() => win(d)}><EmojiEventsIcon fontSize="small" className={classes.winIcon} /></IconButton></Tooltip>
                    <Tooltip title="Marcar perdido"><IconButton size="small" onClick={() => openLose(d)}><CancelIcon fontSize="small" className={classes.loseIcon} /></IconButton></Tooltip>
                    <Tooltip title="Excluir"><IconButton size="small" onClick={() => remove(d)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {!stages.length && (
          <Typography variant="body2" color="textSecondary" style={{ margin: 16 }}>
            Nenhum estágio. Configure em <b>Pipelines</b>.
          </Typography>
        )}
      </div>

      {/* deal dialog */}
      <Dialog open={dialog.open} onClose={close} maxWidth="md" fullWidth>
        <DialogTitle>{dialog.editing ? "Editar deal" : "Novo deal"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}><TextField label="Título *" fullWidth value={dialog.form.title} onChange={(e) => setF({ title: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField label="Valor" type="number" fullWidth value={dialog.form.value} onChange={(e) => setF({ value: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                value={selectedClient}
                options={clientOptions}
                getOptionLabel={(o) => clientLabel(o)}
                getOptionSelected={(o, v) => o && v && o.id === v.id}
                filterOptions={(x) => x}
                loading={clientLoading}
                noOptionsText={
                  (clientSearch || "").trim().length < 3
                    ? "Digite ao menos 3 letras"
                    : "Nenhum cliente encontrado"
                }
                onInputChange={(_, val, reason) => {
                  if (reason === "input") setClientSearch(val);
                  if (reason === "clear") setClientSearch("");
                }}
                onChange={(_, val) => {
                  setSelectedClient(val);
                  setF({ clientId: val ? val.id : "" });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cliente"
                    placeholder="Digite 3+ letras para buscar..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {clientLoading ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Responsável" fullWidth value={dialog.form.ownerId} onChange={(e) => setF({ ownerId: e.target.value })}>
                <MenuItem value="">—</MenuItem>
                {users.map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Pipeline" fullWidth value={dialog.form.pipelineId}
                onChange={(e) => {
                  const np = pipelines.find((p) => p.id === +e.target.value);
                  const firstStage = (np?.stages || [])[0]?.id || "";
                  setF({ pipelineId: +e.target.value, stageId: firstStage });
                }}
                disabled={!!dialog.editing}>
                {pipelines.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Estágio" fullWidth value={dialog.form.stageId} onChange={(e) => setF({ stageId: e.target.value })}>
                {(pipelines.find((p) => p.id === +dialog.form.pipelineId)?.stages || []).map((s) =>
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}><TextField label="Previsão de fechamento" type="date" InputLabelProps={{ shrink: true }} fullWidth value={dialog.form.expectedCloseDate || ""} onChange={(e) => setF({ expectedCloseDate: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Moeda" fullWidth value={dialog.form.currency} onChange={(e) => setF({ currency: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField label="Descrição" fullWidth multiline rows={3} value={dialog.form.description || ""} onChange={(e) => setF({ description: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={save}>Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* lose dialog */}
      <Dialog open={loseDlg.open} onClose={() => setLoseDlg({ open: false, dealId: null, lostReasonId: "", note: "" })} maxWidth="sm" fullWidth>
        <DialogTitle>Marcar deal como perdido</DialogTitle>
        <DialogContent>
          <TextField select label="Motivo" fullWidth value={loseDlg.lostReasonId}
            onChange={(e) => setLoseDlg((s) => ({ ...s, lostReasonId: e.target.value }))} margin="normal">
            <MenuItem value="">—</MenuItem>
            {lostReasons.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
          </TextField>
          <TextField label="Observação" fullWidth multiline rows={3} value={loseDlg.note}
            onChange={(e) => setLoseDlg((s) => ({ ...s, note: e.target.value }))} margin="normal" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoseDlg({ open: false, dealId: null, lostReasonId: "", note: "" })}>Cancelar</Button>
          <Button variant="contained" color="secondary" onClick={confirmLose}>Confirmar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Deals;
